import type {
  CategoriaColetor,
  CotacaoPlanejamento,
  DetalheEscopoMacro,
  ParametroCategoria,
  ProjecaoEscopo,
} from "@/modules/financeiro-obras";
import {
  formatClassificacao,
  formatCompetencia,
  formatMoeda,
  formatReaisSimples,
  formatVariacao,
} from "../planejamento-controle.format";
import { COTACAO_PENDENTE_TITULO } from "../planejamento-controle.constants";
import {
  CLASSIFICACAO_PENDENTE,
  FATOR_NAO_APLICADO,
  MATERIAIS_TITLE,
  ROTULO_CC_PRINCIPAL,
  NAO_DEFINIDA,
  NAO_INFORMADO,
  NO_VALUE,
  SERVICOS_TITLE,
  TOTAL_GERAL,
} from "./detalhe-escopo-macro.constants";
import type { CategoriaDetalhe, LinhaColetor, VariacaoCusto } from "./types-detalhe-escopo-macro";

const BRL = "BRL";

const testIdDe = (key: string) => `detalhe-escopo-linha-${key}`;

/** Valor em reais, ou `null` quando a moeda é estrangeira e a Obra não tem cotação dela (RN06). */
export function emReais(
  valor: number,
  moeda: string,
  cotacao: CotacaoPlanejamento | null,
): number | null {
  if (moeda === BRL) return valor;
  return cotacao && cotacao.moeda === moeda ? valor * cotacao.valor : null;
}

/**
 * Variação do valor projetado contra o custo padrão (os dois em reais, o que mantém a razão da moeda do valor), só com valor,
 * custo padrão e `usaCustoPadrao = false`; custo padrão zero não gera percentual.
 */
export function calcularVariacao(
  valorEmReais: number | null,
  custoPadrao: number | null | undefined,
  usaCustoPadrao: boolean,
): VariacaoCusto | null {
  if (usaCustoPadrao || valorEmReais === null || !custoPadrao) return null;
  const percentual = ((valorEmReais - custoPadrao) / custoPadrao) * 100;
  return {
    texto: `${formatVariacao(percentual)} vs padrão`,
    tom: percentual > 0 ? "positivo" : "negativo",
  };
}

/** Parcela do fator no total (Figma `composicao-total`); sem fator aplicado, "não aplicado" (Figma 16417:421258). */
export function fatorNoTotal(
  total: number,
  projetadoReais: number | null,
  percentualFator: string | null,
): string | null {
  if (percentualFator === null) return FATOR_NAO_APLICADO;
  if (projetadoReais === null) return null;
  return `Fator de idade (${percentualFator}): ${formatMoeda(total - projetadoReais, BRL)}`;
}

export function categoriaSemProjecao(titulo: string): CategoriaDetalhe {
  return {
    titulo,
    valor: NAO_INFORMADO,
    conversao: null,
    variacao: null,
    distribuicao: NAO_DEFINIDA,
    curva: NAO_DEFINIDA,
    comFator: null,
  };
}

export function rotuloCentroCusto(centro: DetalheEscopoMacro["centroCusto"]): string {
  if (centro.principal === undefined) return centro.nome;
  return centro.principal ? ROTULO_CC_PRINCIPAL : `Centro de custo ${centro.nome}`;
}

export function rotuloClassificacao(classificacao: ProjecaoEscopo["classificacao"]): string {
  return classificacao === "PENDENTE" ? CLASSIFICACAO_PENDENTE : formatClassificacao(classificacao);
}

export function montarCategoria(
  titulo: string,
  parametro: ParametroCategoria | null,
  custoPadrao: number | null | undefined,
  projecao: ProjecaoEscopo,
  cotacao: CotacaoPlanejamento | null,
  comFator: number | null,
): CategoriaDetalhe | null {
  if (!parametro) return null;
  const reais = emReais(parametro.valor, parametro.moeda, cotacao);
  let conversao: string | null = null;
  if (parametro.moeda !== BRL) {
    conversao =
      reais === null
        ? COTACAO_PENDENTE_TITULO(parametro.moeda)
        : `= ${formatMoeda(reais, BRL)} (com o ${parametro.moeda} da Obra)`;
  }

  return {
    titulo,
    valor: `${formatMoeda(parametro.valor, parametro.moeda)} (${parametro.moeda})`,
    conversao,
    variacao: calcularVariacao(reais, custoPadrao, projecao.usaCustoPadrao),
    distribuicao: parametro.distribuicao?.nome ?? NO_VALUE,
    curva: parametro.curva?.nome ?? NO_VALUE,
    comFator: comFator === null ? null : formatMoeda(comFator, BRL),
  };
}

const TITULO_CATEGORIA: Record<CategoriaColetor, string> = {
  MATERIAIS: MATERIAIS_TITLE,
  SERVICOS: SERVICOS_TITLE,
};

/** Categorias na ordem do Figma (Materiais, Serviços), cada uma com seus coletores quando expandida, e o total geral. */
export function montarLinhasColetor(
  detalhe: DetalheEscopoMacro,
  projecao: ProjecaoEscopo,
  abertas: ReadonlySet<string>,
): LinhaColetor[] {
  const { mensalizacao } = projecao;
  const mesesDe = (campo: "servicos" | "materiais" | "total") =>
    mensalizacao.map((mes) => formatReaisSimples(mes[campo]));
  const categorias: [CategoriaColetor, "servicos" | "materiais"][] = [
    ["MATERIAIS", "materiais"],
    ["SERVICOS", "servicos"],
  ];

  const linhas = categorias.flatMap(([categoria, campo]): LinhaColetor[] => {
    const parametro = campo === "servicos" ? projecao.servicos : projecao.materiais;
    if (!parametro) return [];

    const coletores = projecao.coletores.filter((coletor) => coletor.categoria === categoria);
    const expandida = abertas.has(categoria);
    const soma = coletores.reduce((total, coletor) => total + (coletor.valor ?? 0), 0);
    const distribuicao = parametro.distribuicao?.nome;
    const cabecalho: LinhaColetor = {
      key: categoria,
      testId: testIdDe(categoria),
      nome: distribuicao
        ? `${TITULO_CATEGORIA[categoria]} · ${distribuicao}`
        : TITULO_CATEGORIA[categoria],
      total: coletores.length > 0 ? formatReaisSimples(soma) : NO_VALUE,
      meses: mesesDe(campo),
      nivel: "categoria",
      expansivel: coletores.length > 0,
      expandida,
    };
    if (!expandida) return [cabecalho];

    return [
      cabecalho,
      ...coletores.map((coletor): LinhaColetor => ({
        key: coletor.codigo,
        testId: testIdDe(coletor.codigo),
        nome: `${coletor.codigo} - ${coletor.nome}`,
        total: formatReaisSimples(coletor.valor),
        meses: mensalizacao.map((mes) =>
          formatReaisSimples(
            coletor.valores.find((item) => item.competencia === mes.competencia)?.valor,
          ),
        ),
        nivel: "coletor",
        expansivel: false,
        expandida: false,
      })),
    ];
  });

  return [
    ...linhas,
    {
      key: "total-geral",
      testId: testIdDe("total-geral"),
      nome: TOTAL_GERAL,
      total: formatReaisSimples(detalhe.totais?.total),
      meses: mesesDe("total"),
      nivel: "total",
      expansivel: false,
      expandida: false,
    },
  ];
}

export function competenciasDe(projecao: ProjecaoEscopo): string[] {
  return projecao.mensalizacao.map((mes) => formatCompetencia(mes.competencia));
}
